
import { IVehicle } from "@interfaces/IVehicle";
import * as angular from "angular";
import * as moment from "moment";
import { VehicleModel } from "../../../model/VehicleModel";
import "./messagingConversation.scss";
import { Conversation } from "./models/conversation";
import { ExpiryOptionsModel } from "./models/ExpiryOptionsModel";
import { IMessageModel } from "./models/IMessageModel";
import { MessageService } from "./service/messaging.service";



interface IMessagingConversationComponentBindings {

    vehicle: IVehicle;
    hideHeader: boolean;
    updated(messages: { messages: IMessageModel[] });
    onMarkedAsRead(): void;
}

class MessagingConversationComponentController implements angular.IComponentController, IMessagingConversationComponentBindings {
    vehicle: IVehicle;
    hideHeader: boolean;
    updated: (messages: { messages: IMessageModel[] }) => void;
    onMarkedAsRead: () => void;

    loading: boolean;
    conversation: Conversation;

    messageReplyBody = "";
    messageReplyExpiryId: number;

    expiryOptions: ExpiryOptionsModel[] = [];

    scrollToBottom = 1;
    conversationUpdateIntervalPromise: angular.IPromise<any>;

    pageSize = 100;
    updateInterval = 630 * 1000; // X Seconds between updates

    static $inject = ["$q", "$interval", "$filter", "messageService"];

    constructor(
        private $q: angular.IQService,
        private $interval: angular.IIntervalService,
        private $filter: angular.IFilterService,
        private messageService: MessageService) {

        this.setupExpiryOptions();
    }

    $onInit(): void {
        this.conversationUpdateIntervalPromise = this.$interval(() => {
            this.loadMessages(1, this.pageSize, true, true);
        }, this.updateInterval);
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        if (onChangesObj["vehicle"] && this.vehicle) {
            this.onVehicleChanged();
        }
    }

    $onDestroy(): void {
        if (this.conversationUpdateIntervalPromise) {
            this.$interval.cancel(this.conversationUpdateIntervalPromise);
        }
    }

    sendVehicleMessage() {
        // Prevent blank messages
        if (this.messageReplyBody.length === 0) {
            return;
        }

        const newMessage = this.messageService.createMessageModel(this.messageReplyBody, this.messageReplyExpiryId);
        newMessage.VehicleId = this.vehicle.Id;

        // Clear our form text
        this.messageReplyBody = "";

        this.sendMessageToVehicle(newMessage)
            .then(() => {
                //this.updateLatestMessages();
                this.sendUpdates();
            });
    }

    readMessage(message: IMessageModel) {
        if (message.Originator === 0 || message.MsgCode === 6) {
            return;
        }

        message.MsgCode = 6;

        this.messageService
            .setMessageToRead(message.Id)
            .then(() => {
                //this.updateMessageUnreadCounts();
                this.conversation.unreadMessageCount -= 1;
                this.emitMarkedAsRead();
                this.sendUpdates();
            });
    }

    markConversationAsRead(conversation: Conversation) {

        conversation.messages.forEach(message => {
            if (message.Originator === 1 && message.MsgCode !== 6) {
                message.MsgCode = 6;
            }
        });

        if (conversation.unreadMessageCount) {
            this.messageService
                .setConversationMessagesToRead(conversation.vehicle.Id)
                .then(() => {
                    this.conversation.unreadMessageCount = 0;
                    this.emitMarkedAsRead();
                    this.sendUpdates();
                });
        }
    }

    private onVehicleChanged() {
        this.resetScrollToBottom();

        this.conversation = new Conversation(this.messageService, this.$filter);
        this.conversation.vehicle = this.vehicle as VehicleModel;

        this.loadMessages(1, this.pageSize, true);
    }

    private loadNextMessages(): angular.IPromise<IMessageModel[]> {
        if (!this.conversation) {
            return;
        }

        return this.loadMessages(this.conversation.getNextPageNumber(this.pageSize), this.pageSize);
    }

    private loadMessages(page: number,
        pageSize: number,
        scrollWhenFinished = false,
        silent = false,
        dateFrom?: moment.Moment,
        dateTo?: moment.Moment): angular.IPromise<IMessageModel[]> {

        if (this.loading || this.conversation == null) {
            return this.$q.reject();
        }

        // Silent mode will stop the spinner from showing, useful for ninja updates
        if (!silent) {
            this.loading = true;
        }

        const vehicleId = this.conversation.vehicle.Id;
        //this.updateMessageUnreadCounts();

        return this.messageService
            .getMessagesForVehicle(vehicleId, page, pageSize, dateFrom, dateTo)
            .then(data => {
                this.conversation.applyNewMessages(data);

                if (silent) {
                    this.sendUpdates();
                }

                return data;
            })
            .finally(() => {
                // Needs to be in a timeout to ensure the browser has chance to render the changes

                this.loading = false;

                //this.$timeout(() => {
                if (scrollWhenFinished) {
                    this.resetScrollToBottom();
                }
                //});
            });
    }

    private sendMessageToVehicle(newMessage: IMessageModel): angular.IPromise<any> {
        this.insertNewMessageToConversation(newMessage);

        this.resetScrollToBottom();

        return this.messageService
            .sendMessage(newMessage)
            .then(() => {
                this.loadMessages(1, this.pageSize)
                    .then((messages) => {
                        this.conversation.clearMessages();
                        this.conversation.applyNewMessages(messages);

                        this.sendUpdates();

                        this.resetScrollToBottom();
                    });
            });
    }

    private insertNewMessageToConversation(message: IMessageModel) {
        this.conversation.lastMessage = message;
        this.conversation.messages.unshift(message);
    }

    private resetScrollToBottom() {
        this.scrollToBottom = moment().valueOf();
    }

    private setupExpiryOptions(): void {

        this.expiryOptions = this.messageService.getExpiryOptions();

        this.messageReplyExpiryId = this.expiryOptions[this.expiryOptions.length - 1].id;
    }

    private sendUpdates() {
        if (this.updated) {
            this.updated({ messages: this.conversation.messages });
        }
    }

    private emitMarkedAsRead(): void {
        if (!this.onMarkedAsRead) {
            return;
        }

        this.onMarkedAsRead();
    }
}

export class MessagingConversationComponent implements angular.IComponentOptions {
    bindings: any;
    template: string;
    controller: any;

    constructor() {
        this.bindings = {
            vehicle: "<",
            hideHeader: "@",
            updated: "&",
            onMarkedAsRead: "&"
        };

        this.controller = MessagingConversationComponentController;

        this.template = require("./messagingConversation.html");
    }
}
